import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditCalendar } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CourseCalendar } from "@shared/models/course"
import type { PlanbookCalendar } from "@shared/models/planbook"
import type { RotationCalendarAttributes } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { cloneDeep, compact, concat, map, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"

export interface Payload {
  rotationCalendar: {
    id: string
    originalAttributes: RotationCalendarAttributes
    updatedAttributes: RotationCalendarAttributes
  }

  planbookId: string

  planbook: null | {
    id: string
    originalCalendar: PlanbookCalendar
    updatedCalendar: PlanbookCalendar
  }

  courses: null | Array<{
    id: string
    originalCalendar: CourseCalendar
    updatedCalendar: CourseCalendar
  }>

  changeDescription: string

  causeOfChange:
    | "CHANGE_ROTATION_ID"
    | "RESET_ROTATION"
    | "ADD_IS_OFF"
    | "ADD_NOTE"
    | "SKIP_ROTATION_DAY"
    | "CHANGE_START_END_DATE"
    | "REMOVE_DATE_ANNOTATION"
}

export function changeCalendar(payload: Payload): PreparedAction {
  return {
    name: "CHANGE_CALENDAR_WITH_DATE_ANNOTATION",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let rcPatch = Patch.create("rotation-calendar", payload.rotationCalendar.id)
    .set("attributes.dates", payload.rotationCalendar.updatedAttributes.dates)
    .set("attributes.dateAnnotations", payload.rotationCalendar.updatedAttributes.dateAnnotations)
    .set("attributes.schoolDays", payload.rotationCalendar.updatedAttributes.schoolDays)
    .set("attributes.semesters", payload.rotationCalendar.updatedAttributes.semesters)
    .set("attributes.rotations", payload.rotationCalendar.updatedAttributes.rotations).patch

  let planbookPatch: PatchModel | null = null
  if (payload.planbook !== null) {
    planbookPatch = Patch.create("planbook", payload.planbook.id).set(
      "attributes.calendar",
      payload.planbook.updatedCalendar
    ).patch
  }

  let coursePatches: Array<PatchModel> = []
  if (payload.courses !== null) {
    coursePatches = map(payload.courses, ({ id, updatedCalendar }) => {
      return Patch.create("course", id)
        .set("attributes.calendar.dates", updatedCalendar.dates)
        .set("attributes.calendar.units", updatedCalendar.units)
        .inc("attributes.calendar.version", 1).patch
    })
  }

  // TODO Add patches for creating card stacks

  return compact(concat([rcPatch, planbookPatch], coursePatches))
}

function undoPatches(payload: Payload): Array<PatchModel> {
  let rcPatch = Patch.create("rotation-calendar", payload.rotationCalendar.id)
    .set("attributes.dates", payload.rotationCalendar.originalAttributes.dates)
    .set("attributes.dateAnnotations", payload.rotationCalendar.originalAttributes.dateAnnotations)
    .set("attributes.schoolDays", payload.rotationCalendar.originalAttributes.schoolDays)
    .set("attributes.semesters", payload.rotationCalendar.originalAttributes.semesters)
    .set("attributes.rotations", payload.rotationCalendar.originalAttributes.rotations).patch

  let planbookPatch: PatchModel | null = null
  if (payload.planbook !== null) {
    planbookPatch = Patch.create("planbook", payload.planbook.id).set(
      "attributes.calendar",
      payload.planbook.originalCalendar
    ).patch
  }

  let coursePatches: Array<PatchModel> = []
  if (payload.courses !== null) {
    coursePatches = map(payload.courses, ({ id, originalCalendar }) => {
      return Patch.create("course", id)
        .set("attributes.calendar.dates", cloneDeep(originalCalendar.dates))
        .set("attributes.calendar.units", cloneDeep(originalCalendar.units))
        .inc("attributes.calendar.version", 1).patch
    })
  }

  return compact(concat([rcPatch, planbookPatch], coursePatches))
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let titleForAnalytics: string
  let title: string
  let newEvent: PlanbookEditCalendar | null

  if (payload.causeOfChange === "ADD_IS_OFF") {
    titleForAnalytics = "Rotation Calendar - Add is Off"
    title = "Add Day Off"
    newEvent = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      primary_object_id: payload.planbook?.id || null,
      primary_object_id_and_ancestor_ids: payload.planbook ? [payload.planbook.id] : [],
      primary_verb: "edit",
      primary_possessive:
        payload.planbook !== null
          ? getPossessiveForPlanbook(findFn, userId, payload.planbook.id)
          : null,
      primary_object_type: "planbook",
      primary_object_specifier: "calendar",
      adverbial_verb: "adding",
      adverbial_object_type: "rotation_day_off",
      adverbial_object_specifier: null,
    }
  } else if (payload.causeOfChange === "ADD_NOTE") {
    titleForAnalytics = "Rotation Calendar - Add Note"
    title = "Add Note"
    newEvent = null
  } else if (payload.causeOfChange === "CHANGE_ROTATION_ID") {
    titleForAnalytics = "Rotation Calendar - Changed Rotation Id"
    title = "Changed Rotation Day"
    newEvent = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      primary_object_id: payload.planbook?.id || null,
      primary_object_id_and_ancestor_ids: payload.planbook ? [payload.planbook.id] : [],
      primary_verb: "edit",
      primary_possessive:
        payload.planbook !== null
          ? getPossessiveForPlanbook(findFn, userId, payload.planbook.id)
          : null,
      primary_object_type: "planbook",
      primary_object_specifier: "calendar",
      adverbial_verb: "editing",
      adverbial_object_type: "rotation",
      adverbial_object_specifier: "id",
    }
  } else if (payload.causeOfChange === "RESET_ROTATION") {
    titleForAnalytics = "Rotation Calendar - Reset Rotation"
    title = "Reset Rotation"
    newEvent = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      primary_object_id: payload.planbook?.id || null,
      primary_object_id_and_ancestor_ids: payload.planbook ? [payload.planbook.id] : [],
      primary_verb: "edit",
      primary_possessive:
        payload.planbook !== null
          ? getPossessiveForPlanbook(findFn, userId, payload.planbook.id)
          : null,
      primary_object_type: "planbook",
      primary_object_specifier: "calendar",
      adverbial_verb: "editing",
      adverbial_object_type: "rotation",
      adverbial_object_specifier: "reset",
    }
  } else if (payload.causeOfChange === "SKIP_ROTATION_DAY") {
    titleForAnalytics = "Rotation Calendar - Skip Rotation"
    title = "Skipped Rotation"
    newEvent = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      primary_object_id: payload.planbook?.id || null,
      primary_object_id_and_ancestor_ids: payload.planbook ? [payload.planbook.id] : [],
      primary_verb: "edit",
      primary_possessive:
        payload.planbook !== null
          ? getPossessiveForPlanbook(findFn, userId, payload.planbook.id)
          : null,
      primary_object_type: "planbook",
      primary_object_specifier: "calendar",
      adverbial_verb: "editing",
      adverbial_object_type: "rotation",
      adverbial_object_specifier: "skip",
    }
  } else if (payload.causeOfChange === "REMOVE_DATE_ANNOTATION") {
    titleForAnalytics = "Rotation Calendar - Remove Annotation"
    title = "Removed Calendar Event"
    newEvent = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      primary_object_id: payload.planbook?.id || null,
      primary_object_id_and_ancestor_ids: payload.planbook ? [payload.planbook.id] : [],
      primary_verb: "edit",
      primary_possessive:
        payload.planbook !== null
          ? getPossessiveForPlanbook(findFn, userId, payload.planbook.id)
          : null,
      primary_object_type: "planbook",
      primary_object_specifier: "calendar",
      adverbial_verb: "removing",
      adverbial_object_type: "annotation",
      adverbial_object_specifier: null,
    }
  }
  return {
    activity: {},
    context: {
      planbookId: payload.planbook?.id,
      rotationCalendarId: payload.rotationCalendar.id,
      causeOfChange: payload.causeOfChange,
    },
    description: payload.changeDescription,
    // @ts-ignore causeOfChange cases are not exhaustive
    title: title,
    // @ts-ignore causeOfChange cases are not exhaustive
    titleForAnalytics: titleForAnalytics,
    // @ts-ignore causeOfChange cases are not exhaustive
    newAnalyticsEvents: newEvent ? [newEvent] : [],
  }
}
